import {Component, OnInit, OnDestroy} from '@angular/core';
import {Subject} from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import {StandardWriteAction} from '../../../services/standard-write-action';
import {HttpResponse} from '@angular/common/http';
import {StandardTranslationSnapshot} from '../../../models/standard-translation.model';

import {ActivatedRoute, Router} from '@angular/router';
import {AdminService} from '../../../services/admin.service';
import {ApiConnectService} from '../../../services/api-connect.service';
import {UserService} from '../../../services/user.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ViewDocxService} from '../../../services/view-docx.service';
import {ViewDocxComponent} from '../../views/view-docx/view-docx.component';

const TRANSLATION_OK = 200;
const TRANSLATION_EMPTY = '';
const TRANSLATION_ROUTE_ID = 'id';
const TRANSLATION_MAIN_ROUTE = 'translate-standard/';

@Component({
  selector: 'app-translate-standard',
  templateUrl: './translate-standard.component.html',
  styleUrls: ['./translate-standard.component.css']
})
export class TranslateStandardComponent implements OnInit, OnDestroy {
  readonly writeAction = new StandardWriteAction();
  private cancelled$ = new Subject<void>();
  private destroyed$ = new Subject<void>();

  public isLoaded = false;                         // Show spinner while standard is loading
  public errorMsg = false;                         // Show error message if we can't get standard
  public urlID = '';                               // Get standard ID from URL. Example:
  // http://localhost/translate-standard/5ae194734db9cf5918f9f8ce
  public standard: StandardTranslationSnapshot = null;
  public showCancelBtn = true;                     // Show Cancel button if URL includes 'translate-standard/'
  public standardBaseLanguage = '';                // Save here base standard language
  formData: FormData = new FormData();              // FormData for uploading files
  public disableDoneBtn = false;                   // Make Translate btn. disabled after click
  public showSuccessMsg = false;                   // Show message if Standard successfully translated
  public showErrorMsg = false;                     // Show message if Standard translated with error
  public clearStatus = '';
  public isDocFileValid = false;
  public invalidDataMsg = false;

  constructor(private route: ActivatedRoute,
              private adminService: AdminService,
              private router: Router,
              public viewDocxService: ViewDocxService,
              private modalService: NgbModal,
              private apiConnectService: ApiConnectService,
              private userService: UserService) {
  }

  ngOnInit() {
    this.route.params.takeUntil(this.destroyed$).subscribe(params => {
      this.urlID = params[TRANSLATION_ROUTE_ID]; this.reloadStandard();
    });
  }

  ngOnDestroy(): void {
    this.cancelled$.next(); this.cancelled$.complete();
    this.destroyed$.next(); this.destroyed$.complete();
    this.writeAction.ngOnDestroy();
  }

  reloadStandard(): void {
    this.resetTranslation();
    this.showCancelBtn = this.router.url.includes(TRANSLATION_MAIN_ROUTE);
    this.adminService.getStandardTranslate(this.urlID).takeUntil(this.cancelled$).subscribe(
      response => this.acceptStandard(response),
      () => { this.errorMsg = true; this.isLoaded = true; }
    );
  }

  private resetTranslation(): void {
    this.cancelled$.next();
    this.writeAction.reset();
    this.standard = null;
    this.formData = new FormData();
    this.isDocFileValid = false;
    this.isLoaded = false;
    this.errorMsg = false;
    this.standardBaseLanguage = TRANSLATION_EMPTY;
    this.disableDoneBtn = false;
    this.showSuccessMsg = false;
    this.showErrorMsg = false;
    this.invalidDataMsg = false;

  }

  private acceptStandard(response: HttpResponse<StandardTranslationSnapshot>): void {
    this.isLoaded = true;
    const standard = response.body;
    this.errorMsg = response.status !== TRANSLATION_OK || !standard || standard.id !== this.urlID
      || standard.version == null || !standard.language;
    if (this.errorMsg) { return; }
    this.standard = standard;
    this.standardBaseLanguage = standard.language;
    this.clearStatus = String(standard.status);
  }


  openDocModal(docxID, sID) {
    localStorage.setItem('DocxID', docxID);
    localStorage.setItem('DocxSID', sID);

    const modalRef = this.modalService.open(ViewDocxComponent, {size: 'lg', backdrop: 'static'});
    modalRef.result.then((result) => {
      if (result === 'Close') {
        // on close button
        this.viewDocxService.deleteLinkByDocxID();
      }
    }, (reason) => {
      // on dismiss
      this.viewDocxService.deleteLinkByDocxID();
    });

  }

  openDocModalDOCX(fileLink) {
    localStorage.setItem('fileLink', fileLink);

    const modalRef = this.modalService.open(ViewDocxComponent, {size: 'lg', backdrop: 'static'});
    modalRef.result.then((result) => {
      if (result === 'Close') {
        // on close button
        this.viewDocxService.deleteLinkByDocxIDManually();
      }
    }, (reason) => {
      // on dismiss
      this.viewDocxService.deleteLinkByDocxIDManually();
    });

  }


  // Use it for validation all fields on other language
  isValidFields() {
    if (this.isLoaded) {

      // For russian language
      if (this.standardBaseLanguage.toLowerCase() === 'russian') {
        return (this.standard.enName !== null && this.standard.enName !== ''
          && this.standard.enAnnotation !== null && this.standard.enAnnotation !== ''
          && this.isDocFileValid);

        // For english language
      } else if (this.standardBaseLanguage.toLowerCase() === 'english') {
        return (this.standard.ruName !== null && this.standard.ruName !== ''
          && this.standard.ruAnnotation !== null && this.standard.ruAnnotation !== ''
          && this.isDocFileValid);

      }
    }
  }


  translateStandardMainInfo() {
    if (!this.standard || this.disableDoneBtn || this.writeAction.pending
      || this.writeAction.requiresReload || this.showSuccessMsg) { return; }
    this.invalidDataMsg = false;
    if (this.isValidFields()) {

      this.showSuccessMsg = false;
      this.showErrorMsg = false;


      const dataStandard = this.translationBody();
      this.writeAction.run(this.standard,
        snapshot => this.adminService.doneStandardMainInfo(snapshot, dataStandard),
        () => this.uploadFiles()
      );
    } else {
      this.invalidDataMsg = true;
    }
  }

  private translationBody(): Object {
    return {
        id: this.standard.id,
        number: null,
        authorId: null,
        groupId: null,
        status: this.clearStatus,
        standardType: Object.assign({}, this.standard.standardType),
        standardDisseminationPositions: [],
        ruName: this.standard.ruName,
        ruAnnotation: this.standard.ruAnnotation,
        enName: this.standard.enName,
        enAnnotation: this.standard.enAnnotation,
        language: this.standard.language
    };
  }

  // Go to home page
  onCancelAction() {
    this.router.navigate(['/dashboard/home']);
  }

  // Open .PDF file in new browser tab
  openPDFFile(fileId: string) {
    window.open(this.apiConnectService.getPdf + fileId, '_blank');
  }

  // Download presentation file from server by ID
  downloadPPTXFile(fileId: string) {
    this.userService.getFile(fileId).subscribe(
      (response: any) => {
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(response.body);
        link.download = response.headers.get('filename');
        link.click();
        link.remove();
      },
      () => {
      }
    );
  }

  // Select files for uploading
  fileChange(type: string, event) {
    const fileList = event.target.files;

    console.warn(fileList);

    if (fileList.length > 0) {
      const file: File = fileList[0];
      this.formData.set(type, file, file.name);

      // Validation actions
      if (this.standardBaseLanguage.toLowerCase() === 'russian') {
        this.isDocFileValid = this.formData.has('EN_DOC');
      } else if (this.standardBaseLanguage.toLowerCase() === 'english') {
        this.isDocFileValid = this.formData.has('RU_DOC');
      }
    } else {
      this.formData.delete(type);
      // Validation actions
      if (type === 'RU_DOC' || type === 'EN_DOC') {
        this.isDocFileValid = false;
      }
    }
  }

  // Upload files from formData
  private uploadFiles() {
    this.disableDoneBtn = true;
    this.adminService.uploadFiles(this.standard, this.formData).takeUntil(this.cancelled$).subscribe(
      response => this.acceptUpload(response),
      error => {
        this.writeAction.reject(error);
        this.disableDoneBtn = false;
        this.showSuccessMsg = false;
        this.showErrorMsg = true;
      }
    );
  }

  private acceptUpload(response: HttpResponse<Object>): void {
    this.disableDoneBtn = false;
    this.showSuccessMsg = response.status === TRANSLATION_OK;
    this.showErrorMsg = !this.showSuccessMsg;
    if (this.showErrorMsg) { this.writeAction.reject(); }
  }

}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/translate/translate-standard/translate-standard.component.ts